import { isProductInStock } from 'components/ProductDetails';
import { notifyError } from 'config/setupBugsnag';
import getConfig from 'next/config';
import {
  ApiFilter,
  IProduct,
  JsonApiDocument,
  JsonApiListResponseWithMetaData,
  LanguageBasedVariant,
  Product,
  ProductAsset,
  TBreadCrumb,
} from 'types/';
import RestUtility from '../../restUtility';
import {
  dsoProductsEndPoint,
  favouritesEndPoint,
  productsEndPoint,
} from '../../restUtility/restEndpoints';
import { productCacheService } from 'services';
import { AxiosError } from 'axios';

export class ProductService {
  private restUtility: RestUtility;

  constructor() {
    this.restUtility = new RestUtility();
  }

  async isIDSStoreDisabled(): Promise<{
    isDisabled: boolean;
    disabledReason: string;
  }> {
    try {
      await this.restUtility.get<number[]>(`${favouritesEndPoint}`);

      return { isDisabled: false, disabledReason: '' };
    } catch (error: AxiosError | any) {
      return {
        isDisabled: true,
        disabledReason: error?.response?.data?.error_code ?? 'UNKNOWN',
      };
    }
  }

  isProductCacheEnabled() {
    const { publicRuntimeConfig } = getConfig();
    return publicRuntimeConfig.ENABLE_PRODUCT_CACHE === 'true';
  }

  async getProducts(
    productsFilter: {
      taxonSlug?: string;
      sortDirection?: string;
      variantIds?: string[];
      categoryFilters?: string[];
      skus?: string[];
      pageSize?: number;
    },
    currentPageNumber: number
  ) {
    const { publicRuntimeConfig } = getConfig();
    const perPageSize =
      productsFilter.pageSize ?? publicRuntimeConfig.LISTING_PAGE_SIZE;

    try {
      const includedFields = 'images';
      const productFields =
        'sku,name,slug,display_price,images,default_variant,material_type';
      const imageFields = 'thumbnail_url';

      let sortFilter = '';
      if (productsFilter.sortDirection) {
        sortFilter = `&sort=${productsFilter.sortDirection}`;
      }

      let taxonFilter = '';

      if (
        productsFilter.categoryFilters &&
        productsFilter.categoryFilters.length > 0
      ) {
        taxonFilter = `&filter[taxons]=${productsFilter.categoryFilters.join(
          ','
        )}`;
      } else if (productsFilter.taxonSlug) {
        taxonFilter = `&filter[taxons]=${productsFilter.taxonSlug}`;
      } else {
        taxonFilter = '';
      }

      let variantFilter = '';
      if (productsFilter.variantIds) {
        variantFilter = `&filter[variant_ids]=${productsFilter.variantIds.join(
          ','
        )}`;
      }

      let skuFilter = '';
      if (productsFilter.skus) {
        skuFilter = `&filter[skus]=${productsFilter.skus.join(',')}`;
      }

      if (
        typeof productsFilter.taxonSlug === 'undefined' &&
        productsFilter.variantIds?.length === 0
      ) {
        return {
          products: [],
          filters: [],
          count: 0,
          totalCount: 0,
          totalPages: 0,
          currentPageNumber: currentPageNumber,
        };
      }

      const response =
        await this.restUtility.get<JsonApiListResponseWithMetaData>(
          `${productsEndPoint}?per_page=${perPageSize}${taxonFilter}&page=${currentPageNumber}&include=${includedFields}&fields[product]=${productFields}&fields[image]=${imageFields}${sortFilter}${variantFilter}${skuFilter}`
        );

      return this.buildProducts(response, currentPageNumber);
    } catch (error) {
      notifyError('ApiError:: getProducts', error);
      throw error;
    }
  }

  async getProductDetails(slug: string, isDSOStore = false) {
    if (this.isProductCacheEnabled()) {
      return productCacheService.getProductDetailsUsingCache(slug, isDSOStore);
    } else {
      return this.getProductDetailsUsingServer(slug);
    }
  }

  async getProductDetailsUsingServer(slug: string) {
    try {
      const response = await this.restUtility.get<IProduct>(
        `${productsEndPoint}/${slug}?include=images,taxons,videos&fields[image]=thumbnail_url,original_url`
      );
      return this.buildProductDetailsForClient(response);
    } catch (error) {
      notifyError('ApiError:: getProductDetails', error);
      throw error;
    }
  }

  async getProductQuantityLimit(sku: string, isDSOStore = false) {
    try {
      const endpoint = isDSOStore
        ? `${dsoProductsEndPoint}/${sku}/quantities`
        : `${productsEndPoint}/${sku}/quantities`;
      const response = await this.restUtility.get<number[]>(endpoint);
      return response;
    } catch (error) {
      notifyError('ApiError:: getProductQuantityLimit', error);
      return [];
    }
  }

  async searchProducts(
    searchKeyword: string,
    currentPageNumber: number,
    sortDirection: string
  ) {
    try {
      const productFields =
        'sku,name,slug,display_price,images,default_variant,material_type';
      const imageFields = 'thumbnail_url';
      const { publicRuntimeConfig } = getConfig();
      const perPageSize = publicRuntimeConfig.LISTING_PAGE_SIZE;
      let sortFilter = '';
      if (sortDirection) {
        sortFilter = `&sort=${sortDirection}`;
      }
      const searchByName =
        this.restUtility.get<JsonApiListResponseWithMetaData>(
          `${productsEndPoint}?per_page=${perPageSize}&page=${currentPageNumber}${sortFilter}&filter[name]=${searchKeyword}&include=images&fields[product]=${productFields}&fields[image]=${imageFields}`
        );
      const searchBySku = this.restUtility.get<JsonApiListResponseWithMetaData>(
        `${productsEndPoint}?per_page=${perPageSize}&page=${currentPageNumber}${sortFilter}&filter[skus]=${searchKeyword}&include=images&fields[product]=${productFields}&fields[image]=${imageFields}`
      );
      const bulkResponse = await Promise.all([searchByName, searchBySku]);
      const response =
        bulkResponse[0].data.length > 0 ? bulkResponse[0] : bulkResponse[1];

      return this.buildProducts(response, currentPageNumber);
    } catch (error) {
      notifyError('ApiError:: searchProducts', error);
      throw error;
    }
  }

  async getFavouriteProductIds() {
    try {
      const response = await this.restUtility.get<number[]>(
        `${favouritesEndPoint}`
      );
      return response.map(id => id.toString());
    } catch (error) {
      notifyError('ApiError:: getFavouriteProductIds', error);
      throw error;
    }
  }

  async getProductNameList() {
    try {
      return await this.restUtility.get<{
        data: { sku: string; name: string; slug: string }[];
      }>(`${productsEndPoint}/autosuggest`);
    } catch (error) {
      notifyError('ApiError:: getProductNameList', error);
      throw error;
    }
  }

  async getProductsDetails(variantIds: string[], filterBy: string) {
    try {
      const response =
        await this.restUtility.get<JsonApiListResponseWithMetaData>(
          `${productsEndPoint}?filter[${filterBy}]=${variantIds}&include=images,videos`
        );
      return this.buildProducts(response);
    } catch (error) {
      notifyError('ApiError:: getProductsDetailByVariantIds', error);
      throw error;
    }
  }

  async toggleFavouriteProduct(variantId: number) {
    try {
      const response = await this.restUtility.get<{
        isFavourite: boolean;
      }>(`${favouritesEndPoint}/toggle/${variantId}`);

      return response;
    } catch (error) {
      notifyError('ApiError:: toggleFavouriteProduct', error);
      throw error;
    }
  }

  async downloadAsset(sku: string, dhProjectId?: string) {
    try {
      const response = await this.restUtility.post<{ file_urls: string[] }>(
        `${productsEndPoint}/download`,
        {
          sku: sku,
          include_file: dhProjectId && dhProjectId.length > 0 ? false : true,
          dh_project_id: dhProjectId,
        }
      );

      return response;
    } catch (error) {
      notifyError(`ApiError:: download :: ${sku}`, error);
      throw error;
    }
  }

  buildProductDetailsForClient(response: IProduct) {
    const { publicRuntimeConfig } = getConfig();
    const product: Product = {
      id: '',
      variantId: '',
      name: '',
      sku: '',
      slug: '',
      price: 0.0,
      description: '',
      displayPrice: '',
      quantityLimit: [],
      inStock: true,
      isHavingBulkDiscount: false,
      bulkDiscount: {
        title: '',
        description: '',
      },
      images: [],
      AvailableOn: '',
      breadcrumb: [],
      languageBasedVariants: [],
      assets: [],
      materialType: '',
      isCustomizable: false,
      discontinueOn: '',
      dimensions: '',
    };

    const productQuantityLimit =
      response.data.attributes.quantity_limit?.filter(
        (limitValue: number | null) => limitValue !== null
      );

    const customProductQuantityLimit = productQuantityLimit;

    product.id = response.data.id;
    product.variantId = response.data.relationships.default_variant.data.id;
    product.name = response.data.attributes.name;
    product.sku = response.data.attributes.sku;
    product.displayPrice = response.data.attributes.display_price;
    product.description = response.data.attributes.description;
    product.ingredients = response.data.attributes.ingredients;
    product.instructions = response.data.attributes.instructions;
    product.returnPolicy =
      response.data.attributes.return_policy ||
      publicRuntimeConfig.DEFAULT_RETURN_POLICY;
    product.disclaimerText = response.data.attributes.disclaimer_text;
    product.quantityLimit = customProductQuantityLimit;
    product.inStock = isProductInStock(
      response.data.attributes.backorderable,
      response.data.attributes.in_stock
    );
    product.bulkDiscount.title =
      response.data.attributes?.bulk_discount?.promo_title;
    product.bulkDiscount.description =
      response.data.attributes?.bulk_discount?.promo_description;

    product.isHavingBulkDiscount =
      response.data.attributes.is_having_bulk_discount;

    product.isCustomizable = response.data.attributes.is_customizable;
    product.materialType = response.data.attributes.material_type;
    product.discontinueOn = response.data.attributes.discontinue_on;
    product.dimensions = response.data.attributes.dimensions;

    const images = response?.included?.filter(
      item => item.type === 'image' || item.type === 'video'
    );
    if (images && images.length > 0) {
      images.forEach(productImage => {
        const thumbnail = this.getThumbnailUrl(productImage);
        const original = this.getOriginalImageUrl(productImage);

        product.images.push({
          original: original,
          thumbnail: thumbnail,
          id: productImage.id,
        });
      });
    } else {
      product.images.push({
        id: '-1',
        original: '/images/noimage.jpeg',
        thumbnail: '/images/noimage.jpeg',
      });
    }

    const breadcrumbs = response.data.attributes.breadcrumbs;
    if (breadcrumbs.length) {
      product.breadcrumb = this.buildProductBreadcrumb(breadcrumbs);
    }

    if (response.data.attributes?.language_based_variants?.length > 0) {
      response.data.attributes.language_based_variants.forEach(variant => {
        product.languageBasedVariants.push({
          sku: variant.sku,
          quantity_limit: variant.quantity_limit,
          language: variant.language,
          variantId: variant.variantId,
        });
      });
    }

    if (response.data.attributes?.assets?.length > 0) {
      response.data.attributes.assets.forEach(asset => {
        product.assets.push({
          title: asset.title,
          url: `${publicRuntimeConfig.IMAGE_HOST}${asset.url}`,
        });
      });
    }

    if (response.data.attributes?.product_group) {
      const pg = response.data.attributes.product_group;
      product.productGroup = {
        id: pg.id,
        name: pg.name,
        optionTypes:
          pg.option_types?.map(ot => ({
            id: ot.id,
            name: ot.name,
            presentation: ot.presentation,
          })) ?? [],
        members:
          pg.members?.map(m => ({
            productId: m.product_id,
            variantId: String(m.variant_id),
            name: m.name,
            slug: m.slug,
            sku: m.sku,
            current: m.current,
            position: m.position,
            optionValues:
              m.option_values?.map(ov => ({
                optionTypeId: ov.option_type_id,
                optionTypeName: ov.option_type_name,
                id: ov.id,
                name: ov.name,
                presentation: ov.presentation,
              })) ?? [],
            imageUrl: m.image_url,
            allowedCountries: m.allowed_countries,
          })) ?? [],
      };
    }

    return product;
  }

  buildProductBreadcrumb(breadcrumbs: TBreadCrumb[]) {
    const breadcrumbItems = [...breadcrumbs].sort(
      (item1, item2) => item1.level - item2.level
    );

    const homepage: TBreadCrumb = {
      name: 'Store Home',
      permalink: '/',
      id: -1,
      level: -1,
    };
    breadcrumbItems.unshift(homepage);

    return breadcrumbItems;
  }

  buildProducts(
    response: JsonApiListResponseWithMetaData,
    currentPageNumber?: number
  ) {
    const transformedPrdouctsResponse: {
      products: Product[];
      filters: [];
      count: number;
      totalCount: number;
      totalPages: number;
      currentPageNumber?: number;
    } = {
      products: [],
      filters: [],
      count: 0,
      totalCount: 0,
      totalPages: 0,
      currentPageNumber: currentPageNumber,
    };
    let images: JsonApiDocument[] = [];
    let filters: ApiFilter[] = [];
    const { publicRuntimeConfig } = getConfig();

    if (response.included && response.included?.length > 0) {
      images = response.included.filter(item => item.type === 'image');
    }
    filters = response.meta.filters?.product_properties ?? [];
    const products = response.data;

    products.forEach(product => {
      const productImageIds =
        product.relationships.images.data.map(
          (image: { id: number; type: 'image' }) => image.id
        ) ?? [];

      const productImage = images.find(image =>
        productImageIds.includes(image.id)
      );

      const thumbnail = this.getThumbnailUrl(productImage);

      const original = this.getOriginalImageUrl(productImage);

      const languageBasedVariants: LanguageBasedVariant[] = [];
      const assets: ProductAsset[] = [];

      if (product.attributes?.language_based_variants?.length > 0) {
        product.attributes.language_based_variants.forEach(
          (variant: LanguageBasedVariant) => {
            languageBasedVariants.push({
              sku: variant.sku,
              quantity_limit: variant.quantity_limit,
              language: variant.language,
              variantId: variant.variantId,
            });
          }
        );
      }
      if (product.attributes?.assets?.length > 0) {
        product.attributes.assets.forEach((asset: ProductAsset) => {
          assets.push({
            title: asset.title,
            url: `${publicRuntimeConfig.IMAGE_HOST}${asset.url}`,
          });
        });
      }

      transformedPrdouctsResponse.products.push({
        id: product.id,
        quantityLimit: product.attributes.quantity_limit,
        variantId: product.relationships?.default_variant?.data?.id,
        AvailableOn: product.attributes.available_on,
        slug: product.attributes.slug,
        name: product.attributes.name,
        price: product.attributes.price,
        displayPrice: product.attributes.display_price,
        description: product.attributes.description,
        sku: product.attributes.sku,
        isHavingBulkDiscount: product.attributes.is_having_bulk_discount,
        bulkDiscount: {
          title: '',
          description: '',
        },
        inStock: isProductInStock(
          product.attributes.backorderable,
          product.attributes.in_stock
        ),
        breadcrumb: [],
        ingredients: product.attributes.ingredients,
        returnPolicy: product.attributes.return_policy,
        disclaimerText: product.attributes.disclaimer_text,
        instructions: product.attributes.instructions,
        images: [
          {
            thumbnail: thumbnail,
            original: original,
            id: productImage?.id ?? '-1',
          },
        ],
        languageBasedVariants: languageBasedVariants,
        assets: assets,
        materialType: product.attributes.material_type,
        isCustomizable: product.attributes.is_customizable,
        dimensions: product.attributes.dimensions,
        discontinueOn: product.attributes.discontinue_on,
      });
    });

    transformedPrdouctsResponse.filters = [];

    transformedPrdouctsResponse.count = response.meta?.count;
    transformedPrdouctsResponse.totalCount = response.meta?.total_count;
    transformedPrdouctsResponse.totalPages = response.meta?.total_pages;

    return transformedPrdouctsResponse;
  }

  getThumbnailUrl(productImage: JsonApiDocument | undefined) {
    const { publicRuntimeConfig } = getConfig();
    return productImage
      ? `${publicRuntimeConfig.IMAGE_HOST}${productImage.attributes.thumbnail_url}`
      : '/images/noimage.jpeg';
  }

  getOriginalImageUrl(productImage: JsonApiDocument | undefined) {
    const { publicRuntimeConfig } = getConfig();
    if (!productImage) {
      return '/images/noimage.jpeg';
    }

    const imageUrl =
      productImage.attributes.original_url || productImage.attributes.video_url;

    return imageUrl
      ? `${publicRuntimeConfig.IMAGE_HOST}${imageUrl}`
      : '/images/noimage.jpeg';
  }
}
